import { createSelector } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';
import flattenDeep from 'lodash/flattenDeep';

import { CategoryOpenMode } from '../../models/category/category';
import { CategoryLoadConfig } from '../../services/category/ICategoryService';
import CategoryLayoutViewModel from '../../services/category/view-model/CategoryLayoutViewModel';
import CategoryViewModel, {
  CategoryTreeType,
} from '../../services/category/view-model/CategoryViewModel';
import DatasetLayoutViewModel, {
  DatasetLayoutViewModelType,
  SingleDatasetLayoutViewModel,
} from '../../services/category/view-model/DatasetLayoutViewModel';
import DatasetViewModel from '../../services/category/view-model/DatasetViewModel';
import UniqueId from '../../services/uid/UniqueId';
import { AppState } from '../reducers/root';

import { getDatasetCategoryId } from './dataset';

export const getCategoryState = (
  state: AppState,
  id: UniqueId
): CategoryViewModel => state.category[id].category;

export const getPendingLoadConfig = (
  state: AppState,
  id: UniqueId
): CategoryLoadConfig | undefined => state.category[id].pendingLoad;

export const getCategoryTreeType = (
  state: AppState,
  id: UniqueId
): CategoryTreeType => getCategoryState(state, id).treeType;

export const getCategoryLayoutRoot = (
  state: AppState,
  id: UniqueId
): UniqueId => getCategoryState(state, id).layout.root as UniqueId;

export const getCategoryLayoutElement = (
  state: AppState,
  categoryId: UniqueId,
  elementId: UniqueId
): DatasetLayoutViewModel =>
  getCategoryState(state, categoryId).layout.elements[elementId];

export const getCategoryLayoutActiveTab = (
  state: AppState,
  categoryId: UniqueId,
  tabsElementId: UniqueId
): UniqueId => state.category[categoryId].activeTabs[tabsElementId];

export const getCategoryMasterDataset = createSelector(
  [getCategoryState],
  (category): DatasetViewModel =>
    Object.values(category.datasets).find(
      ({ parent }) => !parent
    ) as DatasetViewModel
);

export const getCategoryOpenMode = (
  state: AppState,
  categoryId: UniqueId
): CategoryOpenMode => state.category[categoryId].openMode;

const getLayoutPath = (
  layout: CategoryLayoutViewModel,
  datasetId: UniqueId
): DatasetLayoutViewModel[] => {
  let el = Object.values(layout.elements).find(
    element =>
      element.type === DatasetLayoutViewModelType.SINGLE &&
      element.dataset === datasetId
  );
  const path: DatasetLayoutViewModel[] = el ? [el] : [];

  while (el && el.id !== layout.root) {
    const elId = el.id;
    el = Object.values(layout.elements).find(
      element =>
        // TODO: figure out if children may be absent to remove `element.children || []` check
        'children' in element && (element.children || []).includes(elId)
    );

    if (el) {
      path.push(el);
    }
  }

  return path;
};

export const isDatasetVisible = createSelector(
  [
    (state: AppState, datasetId: UniqueId) =>
      getDatasetCategoryId(state, datasetId),
    (state: AppState) => state.category,
    (_state: AppState, datasetId: UniqueId) => datasetId,
  ],
  (categoryId, categoryState, datasetId): boolean => {
    const layout = categoryState[categoryId].category.layout;
    const activeTabs = categoryState[categoryId].activeTabs;
    const path = getLayoutPath(layout, datasetId);

    if (!path.length) {
      return true;
    }

    const visiblePath = path.reduce((memo, el, index) => {
      if (
        (path[index + 1] &&
          path[index + 1].type !== DatasetLayoutViewModelType.TABBED) ||
        !path[index + 1]
      ) {
        return [...memo, el];
      }

      return activeTabs[path[index + 1].id] === el.id ? [...memo, el] : [];
    }, [] as DatasetLayoutViewModel[]);

    return path.length === visiblePath.length;
  }
);

export const getAllChildrenDatasetsByLayoutElement = createSelector(
  [
    (state: AppState, categoryId: UniqueId) =>
      getCategoryState(state, categoryId).layout,
    (_state: AppState, _categoryId: UniqueId, elementId: UniqueId) => elementId,
  ],
  (
    layout: CategoryLayoutViewModel,
    elementId: UniqueId
  ): SingleDatasetLayoutViewModel[] =>
    Object.values(layout.elements)
      .filter(({ type }) => type === DatasetLayoutViewModelType.SINGLE)
      .map(el => [
        el,
        getLayoutPath(layout, (el as SingleDatasetLayoutViewModel).dataset),
      ])
      .filter(([, path]: any) => path.find(({ id }: any) => id === elementId))
      .map(([el]) => el) as SingleDatasetLayoutViewModel[]
);

type AggregationDatasetViewModel = DatasetViewModel & {
  children?: DatasetViewModel[];
};

export const getAllChildrenDatasetsFromCategory = createSelector(
  [
    (state: AppState, datasetId: UniqueId) => {
      const categoryId = getDatasetCategoryId(state, datasetId);
      return getCategoryState(state, categoryId).datasets;
    },
    (_state: AppState, datasetId: UniqueId) => datasetId,
  ],
  (
    datasets: Record<UniqueId, DatasetViewModel>,
    datasetId: UniqueId
  ): UniqueId[] => {
    const datasetsClone: Record<UniqueId, AggregationDatasetViewModel> =
      cloneDeep(datasets);

    for (const key of Object.keys(datasetsClone)) {
      datasetsClone[key].children = [
        ...(datasetsClone[key].children || []),
        ...Object.values(datasetsClone).filter(({ parent }) => parent === key),
      ];
    }

    const getChildren = (dataset: any): any => [
      ...(dataset.children || []).map((el: any) => [el.id, ...getChildren(el)]),
    ];

    return flattenDeep(getChildren(datasetsClone[datasetId]));
  }
);

export const getCategoryConditions = createSelector(
  getCategoryState,
  category => category.source.displayModeConditions
);

export const getCategoryTreePanelSize = (
  state: AppState,
  id: UniqueId
): number | undefined => getCategoryState(state, id).layout.treePanelSize;

export const getCategoryNextAction = (
  state: AppState,
  id: UniqueId
): string | undefined => state.category[id].nextAction;

export const getActiveCategoriesNames = createSelector(
  [(state: AppState) => state.category],
  categoryState =>
    Object.values(categoryState).map(
      ({
        category: {
          source: { category },
        },
      }) => category
    )
);
